import { LatLngBounds } from 'leaflet';
import { PageSize, Orientation, MapScale, GridSize } from './exportTypes';
import { createFullPageTemplate } from './paperMapTemplate';
import { renderPaperMapPdf, downloadPdf } from './pdfRenderer';
import { CanvasMapRenderer, RenderBounds, RenderProgress } from './canvasRenderer';
import { MapEntity } from '../mapTypes.d';

// Default tile URL for OpenStreetMap
const DEFAULT_TILE_URL = 'https://tile.openstreetmap.org/{z}/{x}/{y}.png';

// Print DPI for high-quality output
const PRINT_DPI = 300;

interface ExportOptions {
    pageSize: PageSize;
    orientation: Orientation;
    scale: MapScale;
    gridSize: GridSize;
    viewportBounds: {
        southWest: { lat: number; lng: number };
        northEast: { lat: number; lng: number };
    } | null;
    entities: MapEntity[];
    showMgrsGrid: boolean;
    tileUrl?: string;
    onProgress?: (progress: RenderProgress) => void;
}

/**
 * Renders the map viewport independently using the canvas renderer
 * and exports it to a PDF using the paper map template.
 */
// Convert GridSize to interval in meters
const GRID_SIZE_TO_INTERVAL: Record<GridSize, number> = {
    '100km': 100000,
    '1km': 1000,
    '100m': 100,
};

export async function exportMapToPdf(options: ExportOptions): Promise<void> {
    const {
        pageSize,
        orientation,
        scale,
        gridSize,
        viewportBounds,
        entities,
        showMgrsGrid,
        tileUrl = DEFAULT_TILE_URL,
        onProgress,
    } = options;

    if (!viewportBounds) {
        throw new Error('Viewport bounds not available');
    }

    // Create the template based on current settings
    const template = createFullPageTemplate(pageSize, orientation);

    // Calculate output dimensions for 300 DPI print quality
    // Convert mm to pixels: (mm / 25.4) * DPI
    const widthPx = Math.round((template.mapViewport.width / 25.4) * PRINT_DPI);
    const heightPx = Math.round((template.mapViewport.height / 25.4) * PRINT_DPI);

    // Convert viewport bounds to RenderBounds format
    const renderBounds: RenderBounds = {
        north: viewportBounds.northEast.lat,
        south: viewportBounds.southWest.lat,
        east: viewportBounds.northEast.lng,
        west: viewportBounds.southWest.lng,
    };

    // Create and use the canvas renderer
    const renderer = new CanvasMapRenderer();

    try {
        // Render the map to canvas
        const canvas = await renderer.render(
            {
                bounds: renderBounds,
                widthPx,
                heightPx,
                entities,
                showMgrsGrid,
                tileUrl,
                symbolSize: 48, // Larger symbols for print quality
                gridInterval: GRID_SIZE_TO_INTERVAL[gridSize],
            },
            onProgress
        );

        // Convert canvas to data URL
        const mapImageDataUrl = renderer.canvasToDataUrl(canvas, 'image/png', 0.92);

        // Convert serializable bounds to LatLngBounds for PDF renderer
        const latLngBounds = new LatLngBounds(
            [viewportBounds.southWest.lat, viewportBounds.southWest.lng],
            [viewportBounds.northEast.lat, viewportBounds.northEast.lng]
        );

        // Render the PDF using the template
        const pdf = renderPaperMapPdf({
            template,
            scale,
            viewportBounds: latLngBounds,
            mapImageDataUrl,
        });

        // Download the PDF
        downloadPdf(pdf);
    } finally {
        // Clear caches to free memory
        renderer.clearCaches();
    }
}
